export type StickerAttributionQuery = {
  socialShopId?: string | null;
  glowId?: string | null;
};

/** Query keys forwarded when navigating between stickers in a shop or embed context */
export const STICKER_NAV_QUERY_KEYS = [
  'socialShopId',
  'glowId',
  'embedOrganizationId',
  'embedSource',
  'standalone',
] as const;

export function pickStickerAttributionQuery(
  query: Record<string, unknown>,
): StickerAttributionQuery {
  return {
    socialShopId: (query.socialShopId as string) || null,
    glowId: (query.glowId as string) || null,
  };
}

export function appendStickerAttributionQuery(
  baseUrl: string,
  attribution: StickerAttributionQuery,
): string {
  const params = new URLSearchParams();

  if (attribution.socialShopId) {
    params.set('socialShopId', String(attribution.socialShopId));
  }
  if (attribution.glowId) {
    params.set('glowId', String(attribution.glowId));
  }

  const query = params.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
}

/** Sticker detail URL for collection code entry (preserves embed + shop/glow query). */
export function buildStickerOpenCollectionCodeLocation(
  slugOrId: string,
  routeQuery: Record<string, unknown>,
  isEmbeddedPath: boolean,
): { path: string; query: Record<string, string> } {
  const path = isEmbeddedPath
    ? `/web/embed/sticker/${slugOrId}`
    : `/web/sticker/${slugOrId}`;

  const query: Record<string, string> = {};
  STICKER_NAV_QUERY_KEYS.forEach((key) => {
    const value = routeQuery[key];
    if (value != null && value !== '') {
      query[key] = String(value);
    }
  });
  query.openCollectionCode = '1';

  return { path, query };
}

export function buildStickerShareUrl(
  slugOrId: string,
  attribution?: StickerAttributionQuery,
): string {
  const base = `${import.meta.env.VITE_WEB_BASE_URL}/web/sticker/${slugOrId}`;
  return appendStickerAttributionQuery(base, attribution ?? {});
}

export function buildStickerPathWithAttribution(
  slugOrId: string,
  attribution?: StickerAttributionQuery,
): string {
  const base = `/web/sticker/${slugOrId}`;
  return appendStickerAttributionQuery(base, attribution ?? {});
}

export function copyStickerNavQueryParams(
  query: Record<string, unknown>,
): URLSearchParams {
  const params = new URLSearchParams();

  STICKER_NAV_QUERY_KEYS.forEach((key) => {
    const value = query[key];
    if (value != null && value !== '') {
      params.set(key, String(value));
    }
  });

  return params;
}

const HTML_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
};

/** Plain text for a share sheet from a rich-text (HTML) description: no tags, entities decoded. */
export function htmlToShareText(html: string | null | undefined, maxLength = 280): string {
  if (!html) return '';
  const text = html
    .replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#\d+|#x[\da-f]+|[a-z]+);/gi, (entity, code: string) => {
      if (code[0] === '#') {
        const hex = code[1]?.toLowerCase() === 'x';
        return String.fromCodePoint(parseInt(code.slice(hex ? 2 : 1), hex ? 16 : 10));
      }
      return HTML_ENTITIES[code.toLowerCase()] ?? entity;
    })
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
}
